// src/components/draggable_video_player.tsx
"use client";
import React, { useState, useRef, useEffect } from "react";
import { Play, X, Maximize2, Minimize2 } from "lucide-react";

interface Video {
  id: string;
  title: string;
  loomId: string;
}

interface DraggableVideoPlayerProps {
  videos: Video[];
  className?: string;
  buttonText?: string;
}

const DraggableVideoPlayer: React.FC<DraggableVideoPlayerProps> = ({
  videos,
  className = "",
  buttonText,
}) => {
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState({ width: 1120, height: 630 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const videoRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (
      (e.target as HTMLElement).closest(".resize-handle") ||
      (e.target as HTMLElement).closest(".video-content")
    )
      return;

    setIsDragging(true);
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
    if (isResizing && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = Math.max(300, e.clientX - rect.left);
      const newHeight = Math.max(200, e.clientY - rect.top);
      setSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  const openVideo = (video: Video) => {
    setCurrentVideo(video);
    setIsVideoVisible(true);
  };

  const closeVideo = () => {
    setIsVideoVisible(false);
    setCurrentVideo(null);
  };

  const toggleSize = () => {
    if (size.width > 600) {
      setSize({ width: 400, height: 225 });
    } else {
      setSize({ width: 800, height: 450 });
    }
  };

  return (
    <div className={className}>
      {/* Video Selection Buttons */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          {videos.map((video) => (
            <button
              key={video.id}
              onClick={() => openVideo(video)}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
            >
              <Play size={14} />
              {buttonText || video.title}
            </button>
          ))}
        </div>
      </div>

      {/* Draggable Video Player */}
      {isVideoVisible && currentVideo && (
        <div
          ref={containerRef}
          className="fixed bg-background rounded-lg shadow-2xl border border-border z-50"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            width: `${size.width}px`,
            cursor: isDragging ? "grabbing" : "grab",
          }}
          onMouseDown={handleMouseDown}
        >
          {/* Video Header */}
          <div className="flex items-center justify-between p-3 bg-muted rounded-t-lg border-b">
            <h3 className="font-semibold text-foreground truncate flex-1 mr-4">
              {currentVideo.title}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleSize}
                className="p-1 hover:bg-muted-foreground/10 rounded transition-colors"
                title="Toggle size"
              >
                {size.width > 600 ? (
                  <Minimize2 size={16} />
                ) : (
                  <Maximize2 size={16} />
                )}
              </button>
              <button
                onClick={closeVideo}
                className="p-1 hover:bg-muted-foreground/10 rounded transition-colors"
                title="Close video"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Video Content */}
          <div className="video-content p-4">
            <div
              className="relative bg-black rounded"
              style={{ height: `${size.height - 100}px` }}
            >
              {/* Loom embed iframe */}
              <iframe
                ref={videoRef}
                src={`https://www.loom.com/embed/${currentVideo.loomId}`}
                className="w-full h-full rounded"
                frameBorder="0"
                allowFullScreen
                title={currentVideo.title}
              />
            </div>
          </div>

          {/* Resize Handle */}
          <div
            className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-muted hover:bg-muted-foreground/20 transition-colors"
            style={{
              clipPath: "polygon(100% 0%, 0% 100%, 100% 100%)",
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsResizing(true);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DraggableVideoPlayer;
